/** @jsx jsx */

import { ChangeEvent, Fragment } from 'react';

import {
  Alert,
  AlertDescription,
  Field,
  FormLabel,
  Grid,
  LabeledRadio,
  Margin,
  Tag,
  Text,
  TextInput,
} from '@zapier/design-system';
import { css, jsx } from '@emotion/core';

import DoubleDropdowns from 'app/developer-v3/components/DoubleDropdowns';
import RangeSlider from 'app/developer-v3/components/RangeSlider';
import { MIGRATION_TYPES } from 'app/developer-v3/pages/versions/constants';
import { MigrationType } from 'app/developer-v3/pages/versions/types';
import { sortVersionStrings } from 'app/developer-v3/utils';

const Styles = {
  percentageLabel: css`
    white-space: nowrap;
  `,
  wrapper: css`
    margin: 20px 0px;
  `,
};

type Props = {
  email: string;
  fromVersion?: string;
  numUsers: number;
  onChangeEmail: (value: string) => void;
  onChangePercentage: (value: number) => void;
  onChangeType: (value: MigrationType) => void;
  onSetFromVersion: (value: { value: string }) => void;
  onSetToVersion: (value: { value: string }) => void;
  percentage: number;
  toVersion?: string;
  type: MigrationType;
  versions: string[];
};

const UserMigration = (props: Props) => {
  const {
    email,
    fromVersion,
    numUsers,
    onChangeEmail,
    onChangePercentage,
    onChangeType,
    onSetFromVersion,
    onSetToVersion,
    percentage,
    toVersion,
    type,
    versions,
  } = props;

  const isPercentage = type === MIGRATION_TYPES.percentage;
  const isEmail = type === MIGRATION_TYPES.email;

  const items = sortVersionStrings(versions).map(v => ({ value: v, label: v }));

  return (
    <Fragment>
      <DoubleDropdowns
        leftDropdown={{
          title: 'From Version',
          items,
          value: fromVersion,
          onSetValue: onSetFromVersion,
        }}
        rightDropdown={{
          title: 'To Version',
          items,
          value: versions.includes(toVersion) ? toVersion : '',
          onSetValue: onSetToVersion,
        }}
      />
      <div css={Styles.wrapper}>
        <Text margin="20px 0px" tag="h3" type="Title">
          Which user(s) to migrate
        </Text>
        <Grid alignItems="center" columns="repeat(2, max-content)" gap={20}>
          <FormLabel isSelected={isPercentage}>
            <LabeledRadio
              aria-label="Migrate a percentage of users"
              checked={isPercentage}
              name="radio"
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                event.currentTarget.checked &&
                onChangeType(MIGRATION_TYPES.percentage)
              }
              tabIndex={0}
              value="percentage"
            >
              Percentage
            </LabeledRadio>
          </FormLabel>
          <FormLabel isSelected={isEmail}>
            <LabeledRadio
              aria-label="Migrate a single user"
              checked={isEmail}
              name="radio"
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                event.currentTarget.checked &&
                onChangeType(MIGRATION_TYPES.email)
              }
              tabIndex={0}
              value="email"
            >
              Email
            </LabeledRadio>
          </FormLabel>
        </Grid>
        {isPercentage ? (
          <Text margin="20px 0px" tag="p">
            Migrate a percentage of users.
          </Text>
        ) : (
          <Text margin="20px 0px" tag="p">
            {`Migrate one user at a time. The user’s Zaps running on the version ${fromVersion} will be migrated assuming the Zap, as well as the authentication connections in its steps, aren’t shared with other users.`}
          </Text>
        )}
        {isEmail && (
          <Margin margin="20px 0px">
            <Alert title="Zaps to migrate">
              <AlertDescription>
                Migrating a single user will only migrate Zaps that are private
                for that user. Zaps that are shared across the team, shared app
                connections, or in a team/enterprise account will not be
                migrated with this method.
              </AlertDescription>
            </Alert>
          </Margin>
        )}
        {isPercentage ? (
          <Grid alignItems="center" columns="2fr 0.5fr" gap={20}>
            <RangeSlider
              max={100}
              min={5}
              onChange={onChangePercentage}
              step={5}
              value={percentage}
            />
            <Tag>
              <span css={Styles.percentageLabel}>
                {`${percentage}% (~${Math.round(
                  (percentage / 100) * numUsers
                )} users)`}
              </span>
            </Tag>
          </Grid>
        ) : (
          <Field
            label="Email"
            renderInput={inputProps => (
              <TextInput
                {...inputProps}
                aria-label="A user’s email to migrate"
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  onChangeEmail(event.target.value)
                }
                placeholder="example@example.com"
                value={email}
              />
            )}
          />
        )}
      </div>
    </Fragment>
  );
};

UserMigration.defaultProps = {
  email: '',
  percentage: 100,
};

export default UserMigration;
